import { useEffect, useState } from 'react';
import { Card, CardContent, Grid, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { RootState } from '../../../reducers';
import { useNavigate } from 'react-router';
import { hedisMetricSummary } from './MetricSummaryService';
import HedisMetricsView from '../../../Models/MetricSummary/HedisMetricsView.model';
import Authorization from '../../../utils/Authorization';
import { setIsWidgetExisting, setWidgetFromMetricSummary } from '../../../store/smartDashboard.slice';
import { allWidgetsList } from '../Shared/AllWidgetsList';
import { WidgetsEnum } from '../../../utils/constants';
import { copyObject } from '../../../utils/common';
import { useHedisMetricsContext } from '../../../hooks/hedisMetricsContext';
import { useWidgetContext } from '../../../hooks/widgetContext';
import InfoIcon from '@mui/icons-material/Info';
import { TimePeriodFilterEnum } from '../../../Enum/TimePeriodFilterEnum';
import { MetricTypeEnum } from '../../../Enum/MetricTypeEnum';
import { setShowMessage } from '../../../store/shared.slice';
import { fetchTeams } from '../../../store/careTeams.slice';
import TeamDropdown from '../../Shared/TeamDropdown';
import '../../Widgets/Widgets.css';

const HedisMetricSummary = () => {
    const dispatch = useAppDispatch();
    const history = useNavigate();
    const authData = new Authorization();
    const isCEO = authData.Role === 'CEO' ? true : false;
    const { customTabsList } = useSelector((state: RootState) => state.smartDashboard);
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const [providerTeam, setProviderTeam] = useState('All');

    useEffect(() => {
        dispatch(fetchTeams(true));
    }, [dispatch]);

    const {
        selectedTeam,
        setSelectedTeam,
        showInActiveTeams,
        setShowInActiveTeams,

        setAdultHealthHealthPlans,
        setAdultHealthTeam,
        setAdultHealthDateFilter,
        setAdultHealthIncludePastOpenStatus,
        setAdultHealthShowInActiveTeam,

        setBpPopulation,
        setBpTeam,
        setBpDateFilter,
        setBpIncludePastOpenStatus,
        setBpShowInActiveTeam,

        setBmiHealthPlans,
        setBmiTeam,
        setBmiDateFilter,
        setBmiIncludePastOpenStatus,
        setBmiShowInActiveTeam,

        setHgba1cPopulation,
        setHgba1cTeam,
        setHgba1cDateFilter,
        setHgba1cIncludePastOpenStatus,
        setHgba1cShowInActiveTeam,

        setDiabeticEyeExamHealthPlans,
        setDiabeticEyeExamTeam,
        setDiabeticEyeExamDateFilter,
        setDiabeticEyeExamIncludePastOpenStatus,
        setDiabeticEyeExamShowInActiveTeam,

        setMedicationReconciliationHealthPlans,
        setMedicationReconciliationTeam,
        setMedicationReconciliationDateFilter,
        setMedicationReconciliationIncludePastOpenStatus,
        setMedicationReconciliationShowInActiveTeam,

        setDepressionScreeningHealthPlans,
        setDepressionScreeningTeam,
        setDepressionScreeningDateFilter,
        setDepressionScreeningIncludePastOpenStatus,
        setDepressionScreeningShowInActiveTeam,

        setDepressionFollowUpHealthPlans,
        setDepressionFollowUpTeam,
        setDepressionFollowUpDateFilter,
        setDepressionFollowUpIncludePastOpenStatus,
        setDepressionFollowUpShowInActiveTeam,

        setCervicalCancerScreeningHealthPlans,
        setCervicalCancerScreeningTeam,
        setCervicalCancerScreeningDateFilter,
        setCervicalCancerScreeningIncludePastOpenStatus,
        setCervicalCancerScreeningShowInActiveTeam,

        setColorectalCancerScreeningHealthPlans,
        setColorectalCancerScreeningTeam,
        setColorectalCancerScreeningDateFilter,
        setColorectalCancerScreeningIncludePastOpenStatus,
        setColorectalCancerScreeningShowInActiveTeam,

        setBhDiagnosticHealthPlans,
        setBhDiagnosticTeam,
        setBhDiagnosticDateFilter,
        setBhDiagnosticIncludePastOpenStatus,
        setBhDiagnosticShowInActiveTeam,

        setLipidForBhDxHealthPlans,
        setLipidForBhDxTeam,
        setLipidForBhDxDateFilter,
        setLipidForBhDxIncludePastOpenStatus,
        setLipidForBhDxShowInActiveTeam,

        setHgbA1cForBhDxHealthPlans,
        setHgbA1cForBhDxTeam,
        setHgbA1cForBhDxDateFilter,
        setHgbA1cForBhDxIncludePastOpenStatus,
        setHgbA1cForBhDxShowInActiveTeam,
    } = useHedisMetricsContext();

    useEffect(() => {
        if (!Boolean(selectedTeam)) {
            setSelectedTeam(authData.getDefaultTeamId(isCEO ? 'All' : ''));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCEO]);

    const {
        selectedProvider,
        showInActiveProviders,

        setAppointmentSelectedProvider,
        setAppointmentShowInActiveProviders,

        setEngagementSelectedTeam,
        setEngagementShowInActiveTeams,

        setIndividualEngagementSelectedProvider,
        setIndividualEngagementShowInActiveProviders,

        setClosedSelectedFilter,
        setClosedDateFilter,

        setTcTeam,
        setTcShowInActiveTeams,
    } = useWidgetContext();

    const [hgbA1CMetric, setHgbA1CMetric] = useState<HedisMetricsView>(new HedisMetricsView());
    const [bpMetric, setBpMetric] = useState<HedisMetricsView>(new HedisMetricsView());
    const [bmiMetric, setBmiMetric] = useState<HedisMetricsView>(new HedisMetricsView());
    const [adultHealthMetric, setAdultHealthMetric] = useState<HedisMetricsView>(new HedisMetricsView());
    const [depressionScreenMetric, setDepressionScreenMetric] = useState<HedisMetricsView>(new HedisMetricsView());
    const [depressionFollowUpMetric, setDepressionFollowUpMetric] = useState<HedisMetricsView>(new HedisMetricsView());
    const [medicationMetric, setMedicationMetric] = useState<HedisMetricsView>(new HedisMetricsView());
    const [fuhMetric, setFuhMetric] = useState<HedisMetricsView>(new HedisMetricsView());
    const [cervicalCancerScreeningMetric, setCervicalCancerScreeningMetric] = useState<HedisMetricsView>(new HedisMetricsView());
    const [colorectalCancerScreeningMetric, setColorectalCancerScreeningMetric] = useState<HedisMetricsView>(new HedisMetricsView());
    const [bhDiagnosticForSmiMetric, setBhDiagnosticForSmiMetric] = useState<HedisMetricsView>(new HedisMetricsView());

    const [isHedisMetricShow, setIsHedisMetricShow] = useState<boolean>(false);

    const [isHgbA1CTooltipShow, setIsHgbA1CTooltipShow] = useState<boolean>(false);
    const [isBpTooltipShow, setIsBpTooltipShow] = useState<boolean>(false);
    const [isBmiTooltipShow, setIsBmiTooltipShow] = useState<boolean>(false);
    const [isAdultHealthTooltipShow, setIsAdultHealthTooltipShow] = useState<boolean>(false);
    const [isDepressionScreenTooltipShow, setIsDepressionScreenTooltipShow] = useState<boolean>(false);
    const [isDepressionFollowUpTooltipShow, setIsDepressionFollowUpTooltipShow] = useState<boolean>(false);
    const [isMedicationTooltipShow, setIsMedicationTooltipShow] = useState<boolean>(false);
    const [isFuhTooltipShow, setIsFuhTooltipShow] = useState<boolean>(false);
    const [isCervicalCancerScreenTooltipShow, setIsCervicalCancerScreenTooltipShow] = useState<boolean>(false);
    const [isColorectalCancerScreenTooltipShow, setIsColorectalCancerScreenTooltipShow] = useState<boolean>(false);
    const [isBhDiagnosticTooltipShow, setIsBhDiagnosticTooltipShow] = useState<boolean>(false);

    useEffect(() => {
        setIsHedisMetricShow(false);
        const changeFilter = async () => {
            const data = await hedisMetricSummary(selectedTeam, showInActiveTeams);
            if (Boolean(data) && data.length) {
                setHgbA1CMetric(data.find((m) => m.MetricType === MetricTypeEnum.HgbA1C));
                setBpMetric(data.find((m) => m.MetricType === MetricTypeEnum.BloodPressure));
                setBmiMetric(data.find((m) => m.MetricType === MetricTypeEnum.BMI));
                setAdultHealthMetric(data.find((m) => m.MetricType === MetricTypeEnum.AdultHealth));
                setDepressionFollowUpMetric(data.find((m) => m.MetricType === MetricTypeEnum.DepressionFollowUp));
                setDepressionScreenMetric(data.find((m) => m.MetricType === MetricTypeEnum.DepressionScreen));
                setMedicationMetric(data.find((m) => m.MetricType === MetricTypeEnum.MedicationReconciliation));
                setFuhMetric(data.find((m) => m.MetricType === MetricTypeEnum.Fuh));
                setCervicalCancerScreeningMetric(data.find((m) => m.MetricType === MetricTypeEnum.CervicalCancerScreen));
                setColorectalCancerScreeningMetric(data.find((m) => m.MetricType === MetricTypeEnum.ColorectalCancerScreen));
                setBhDiagnosticForSmiMetric(data.find((m) => m.MetricType === MetricTypeEnum.BHDiagnosticCompletedForSMIandBHPrimary));
            }
            setIsHedisMetricShow(true);
        };

        changeFilter();
    }, [selectedTeam, showInActiveTeams, authData.TeamId]);

    useEffect(() => {
        if (Boolean(selectedProvider)) {
            const selectedTeamInfo = teams.find((t) => t.TeamRoles.find((tr) => tr.AssignedProviderId === selectedProvider));
            if (Boolean(selectedTeamInfo)) {
                setProviderTeam(selectedTeamInfo.Id);
            }
        }
    }, [selectedProvider, teams]);

    const navigateToCustomDashboard = (item: string) => {
        if (customTabsList?.length > 0) {
            let tabList = copyObject(customTabsList);
            let homeTab = tabList[0];
            if (Boolean(homeTab)) {
                let component = null;
                let isExistingItem = true;
                switch (item) {
                    case 'HgbA1C':
                        component = allWidgetsList.find((w) => w.name === WidgetsEnum.HGB_A1C_SUMMARY);
                        isExistingItem = homeTab.visibleWidgets.some((m) => m === component?.name);
                        dispatch(setWidgetFromMetricSummary('Hgb A1C Summary'));
                        break;
                    case 'Diabetic Eye Exam':
                        component = allWidgetsList.find((w) => w.name === WidgetsEnum.DIABETIC_EYE_EXAM);
                        isExistingItem = homeTab.visibleWidgets.some((m) => m === component?.name);
                        dispatch(setWidgetFromMetricSummary('Diabetic Eye Exam Summary'));
                        break;
                    case 'Controlled Blood Pressure':
                        component = allWidgetsList.find((w) => w.name === WidgetsEnum.BP_SUMMARY);
                        isExistingItem = homeTab.visibleWidgets.some((m) => m === component?.name);
                        dispatch(setWidgetFromMetricSummary('BP Summary'));
                        break;
                    case 'BMI':
                        component = allWidgetsList.find((w) => w.name === WidgetsEnum.BMI_SUMMARY);
                        isExistingItem = homeTab.visibleWidgets.some((m) => m === component?.name);
                        dispatch(setWidgetFromMetricSummary('BMI Summary'));
                        break;
                    case 'Adult Health':
                        component = allWidgetsList.find((w) => w.name === WidgetsEnum.ADULT_HEALTH_SUMMARY);
                        isExistingItem = homeTab.visibleWidgets.some((m) => m === component?.name);
                        dispatch(setWidgetFromMetricSummary('Adult Health Summary'));
                        break;
                    case 'Depression Screen':
                        component = allWidgetsList.find((w) => w.name === WidgetsEnum.DEPRESSION_SCREENING_SUMMARY);
                        isExistingItem = homeTab.visibleWidgets.some((m) => m === component?.name);
                        dispatch(setWidgetFromMetricSummary('Depression Screening Summary'));
                        break;
                    case 'Depression Follow Up':
                        component = allWidgetsList.find((w) => w.name === WidgetsEnum.DEPRESSION_FOLLOW_UP_SUMMARY);
                        isExistingItem = homeTab.visibleWidgets.some((m) => m === component?.name);
                        dispatch(setWidgetFromMetricSummary('Depression Follow-Up Summary'));
                        break;
                    case 'Medication Reconciliation':
                        component = allWidgetsList.find((w) => w.name === WidgetsEnum.MEDICATION_RECONCILIATION_SUMMARY);
                        isExistingItem = homeTab.visibleWidgets.some((m) => m === component?.name);
                        dispatch(setWidgetFromMetricSummary('Medication Reconciliation Summary'));
                        break;
                    case 'FUH':
                        component = allWidgetsList.find((w) => w.name === WidgetsEnum.TRANSITIONAL_CARE_SUMMARY);
                        isExistingItem = homeTab.visibleWidgets.some((m) => m === component?.name);
                        dispatch(setWidgetFromMetricSummary('Transitional Care Summary'));
                        break;
                    case 'Cervical Cancer Screen':
                        component = allWidgetsList.find((w) => w.name === WidgetsEnum.CERVICAL_CANCER_SCREENING_SUMMARY);
                        isExistingItem = homeTab.visibleWidgets.some((m) => m === component?.name);
                        dispatch(setWidgetFromMetricSummary('Cervical Cancer Screen Summary'));
                        break;
                    case 'Colorectal Cancer Screen':
                        component = allWidgetsList.find((w) => w.name === WidgetsEnum.COLORECTAL_CANCER_SCREENING_SUMMARY);
                        isExistingItem = homeTab.visibleWidgets.some((m) => m === component?.name);
                        dispatch(setWidgetFromMetricSummary('Colorectal Cancer Screen Summary'));
                        break;
                    case 'BH Diagnostic':
                        component = allWidgetsList.find((w) => w.name === WidgetsEnum.BH_DIAGNOSTIC_SUMMARY);
                        isExistingItem = homeTab.visibleWidgets.some((m) => m === component?.name);
                        dispatch(setWidgetFromMetricSummary('BH Diagnostic Summary'));
                        break;
                }

                setHgba1cPopulation(0);
                setHgba1cTeam(selectedTeam);
                setHgba1cDateFilter(TimePeriodFilterEnum.Last6Months);
                setHgba1cIncludePastOpenStatus(true);
                setHgba1cShowInActiveTeam(showInActiveTeams);

                setDiabeticEyeExamHealthPlans(['All']);
                setDiabeticEyeExamTeam(selectedTeam);
                setDiabeticEyeExamDateFilter(TimePeriodFilterEnum.Last6Months);
                setDiabeticEyeExamIncludePastOpenStatus(true);
                setDiabeticEyeExamShowInActiveTeam(showInActiveTeams);

                setBpPopulation(0);
                setBpTeam(selectedTeam);
                setBpDateFilter(TimePeriodFilterEnum.Last6Months);
                setBpIncludePastOpenStatus(true);
                setBpShowInActiveTeam(showInActiveTeams);

                setBmiHealthPlans(['All']);
                setBmiTeam(selectedTeam);
                setBmiDateFilter(TimePeriodFilterEnum.Last6Months);
                setBmiIncludePastOpenStatus(true);
                setBmiShowInActiveTeam(showInActiveTeams);

                setAdultHealthHealthPlans(['All']);
                setAdultHealthTeam(selectedTeam);
                setAdultHealthDateFilter(TimePeriodFilterEnum.Last6Months);
                setAdultHealthIncludePastOpenStatus(true);
                setAdultHealthShowInActiveTeam(showInActiveTeams);

                setDepressionScreeningHealthPlans(['All']);
                setDepressionScreeningTeam(selectedTeam);
                setDepressionScreeningDateFilter(TimePeriodFilterEnum.Last6Months);
                setDepressionScreeningIncludePastOpenStatus(true);
                setDepressionScreeningShowInActiveTeam(showInActiveTeams);

                setDepressionFollowUpHealthPlans(['All']);
                setDepressionFollowUpTeam(selectedTeam);
                setDepressionFollowUpDateFilter(TimePeriodFilterEnum.Last6Months);
                setDepressionFollowUpIncludePastOpenStatus(true);
                setDepressionFollowUpShowInActiveTeam(showInActiveTeams);

                setMedicationReconciliationHealthPlans(['All']);
                setMedicationReconciliationTeam(selectedTeam);
                setMedicationReconciliationDateFilter(TimePeriodFilterEnum.Last6Months);
                setMedicationReconciliationIncludePastOpenStatus(true);
                setMedicationReconciliationShowInActiveTeam(showInActiveTeams);

                setCervicalCancerScreeningHealthPlans(['All']);
                setCervicalCancerScreeningTeam(selectedTeam);
                setCervicalCancerScreeningDateFilter(TimePeriodFilterEnum.Last6Months);
                setCervicalCancerScreeningIncludePastOpenStatus(true);
                setCervicalCancerScreeningShowInActiveTeam(showInActiveTeams);

                setColorectalCancerScreeningHealthPlans(['All']);
                setColorectalCancerScreeningTeam(selectedTeam);
                setColorectalCancerScreeningDateFilter(TimePeriodFilterEnum.Last6Months);
                setColorectalCancerScreeningIncludePastOpenStatus(true);
                setColorectalCancerScreeningShowInActiveTeam(showInActiveTeams);

                setBhDiagnosticHealthPlans(['All']);
                setBhDiagnosticTeam(selectedTeam);
                setBhDiagnosticDateFilter(TimePeriodFilterEnum.Last6Months);
                setBhDiagnosticIncludePastOpenStatus(true);
                setBhDiagnosticShowInActiveTeam(showInActiveTeams);

                setLipidForBhDxHealthPlans(['All']);
                setLipidForBhDxTeam(selectedTeam);
                setLipidForBhDxDateFilter(TimePeriodFilterEnum.Last6Months);
                setLipidForBhDxIncludePastOpenStatus(true);
                setLipidForBhDxShowInActiveTeam(showInActiveTeams);

                setHgbA1cForBhDxHealthPlans(['All']);
                setHgbA1cForBhDxTeam(selectedTeam);
                setHgbA1cForBhDxDateFilter(TimePeriodFilterEnum.Last6Months);
                setHgbA1cForBhDxIncludePastOpenStatus(true);
                setHgbA1cForBhDxShowInActiveTeam(showInActiveTeams);

                setTcTeam(selectedTeam);
                setTcShowInActiveTeams(showInActiveTeams);

                setClosedSelectedFilter('0');
                setClosedDateFilter(TimePeriodFilterEnum.Last6Months);

                setAppointmentSelectedProvider(selectedProvider);
                setAppointmentShowInActiveProviders(showInActiveProviders);

                setEngagementSelectedTeam(providerTeam);
                setEngagementShowInActiveTeams(showInActiveProviders);

                setIndividualEngagementSelectedProvider(selectedProvider);
                setIndividualEngagementShowInActiveProviders(showInActiveProviders);

                dispatch(setIsWidgetExisting(isExistingItem));
                history(`/provider/metricsummary/smart-dashboard`);
            }
        } else {
            dispatch(setShowMessage(true, 'Please add Home tab first', 'error'));
        }
    };

    const handleHgba1cClick = () => {
        setIsHgbA1CTooltipShow(!isHgbA1CTooltipShow);
    };

    const handleBpClick = () => {
        setIsBpTooltipShow(!isBpTooltipShow);
    };

    const handleBmiClick = () => {
        setIsBmiTooltipShow(!isBmiTooltipShow);
    };

    const handleAdultHealthClick = () => {
        setIsAdultHealthTooltipShow(!isAdultHealthTooltipShow);
    };

    const handleDepressionScreenClick = () => {
        setIsDepressionScreenTooltipShow(!isDepressionScreenTooltipShow);
    };

    const handleDepressionFollowUpClick = () => {
        setIsDepressionFollowUpTooltipShow(!isDepressionFollowUpTooltipShow);
    };

    const handleMedicationClick = () => {
        setIsMedicationTooltipShow(!isMedicationTooltipShow);
    };

    const handleFuhClick = () => {
        setIsFuhTooltipShow(!isFuhTooltipShow);
    };

    const handleCervicalCancerClick = () => {
        setIsCervicalCancerScreenTooltipShow(!isCervicalCancerScreenTooltipShow);
    };

    const handleColorectalCancerClick = () => {
        setIsColorectalCancerScreenTooltipShow(!isColorectalCancerScreenTooltipShow);
    };

    const handleBhDiagnosticClick = () => {
        setIsBhDiagnosticTooltipShow(!isBhDiagnosticTooltipShow);
    };

    return (
        <Paper className="smartdashboard-widget" elevation={5}>
            <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                <Grid size={12} textAlign="center">
                    <Typography display={'inline-block'} variant="h6" color={'primary'} className="pointer">
                        Hedis Metric Summary
                    </Typography>
                </Grid>
                {isCEO && (
                    <Grid size={4}>
                        <TeamDropdown
                            teams={teams}
                            selectedTeam={selectedTeam}
                            onChange={(id) => setSelectedTeam(id)}
                            showInactive={showInActiveTeams}
                            onToggleInactive={setShowInActiveTeams}
                        />
                    </Grid>
                )}
            </Grid>
            <Grid container spacing={2} paddingLeft={2} paddingRight={2} marginTop={0.5} minHeight="56vh" sx={{ position: 'relative' }}>
                {!isHedisMetricShow ? (
                    <Box className="loader-center">
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Grid size={4} textAlign="center" paddingLeft="30px">
                            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} sx={{ width: '100%' }}>
                                <Tooltip title="HgbA1C">
                                    <Typography
                                        fontWeight="bold"
                                        variant="body1"
                                        sx={{
                                            minWidth: 50,
                                            maxWidth: '100%',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            textAlign: 'center',
                                        }}
                                    >
                                        HgbA1C
                                    </Typography>
                                </Tooltip>
                                <Tooltip
                                    open={isHgbA1CTooltipShow}
                                    title={
                                        <div>
                                            <Typography variant="body2">For members aged 18-75 who have an ICD 10 of E10-E11</Typography>

                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                Controlled :
                                            </Typography>
                                            <Typography variant="caption">Any of the CPT codes 3051F or 3044F</Typography>
                                        </div>
                                    }
                                    PopperProps={{ style: { zIndex: 1000 } }}
                                >
                                    <IconButton onClick={handleHgba1cClick}>
                                        <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Card
                                sx={{
                                    ':hover': {
                                        cursor: 'pointer',
                                        boxShadow: 10,
                                        border: 0,
                                    },
                                    boxShadow: 0,
                                }}
                                variant="outlined"
                                onClick={() => navigateToCustomDashboard('HgbA1C')}
                            >
                                <CardContent
                                    sx={{
                                        backgroundColor: hgbA1CMetric?.IsMet ? '#82d782' : '#FD5F61',
                                        color: hgbA1CMetric?.IsMet ? 'black' : 'white',
                                    }}
                                >
                                    <Stack spacing={2} alignItems={'center'} justifyContent={'center'}>
                                        <Typography fontWeight={'bold'} variant="h4">
                                            {Boolean(hgbA1CMetric) ? hgbA1CMetric.Percentage : 0}%
                                        </Typography>
                                        <Stack direction={'row'} spacing={2}>
                                            <Typography fontWeight={'bold'} variant="body2">
                                                {hgbA1CMetric?.IsMet ? 'Met' : 'Not Met'}
                                            </Typography>
                                            <Typography fontWeight={'bold'} variant="body2">
                                                (Goal: {Boolean(hgbA1CMetric) ? hgbA1CMetric.Goal : 0}%)
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={4} textAlign="center" paddingLeft="30px">
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                gap={0.5}
                                sx={{ width: '100%', paddingTop: '4px', paddingBottom: '2px' }}
                            >
                                <Tooltip title="Controlled Blood Pressure">
                                    <Typography
                                        fontWeight="bold"
                                        variant="body1"
                                        sx={{
                                            minWidth: 40,
                                            maxWidth: '100%',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            textAlign: 'center',
                                        }}
                                    >
                                        Controlled Blood Pressure
                                    </Typography>
                                </Tooltip>
                                <Tooltip
                                    open={isBpTooltipShow}
                                    title={
                                        <div>
                                            <Typography variant="body2">Member who has an ICD 10 of I10, within a rolling 12 months</Typography>

                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                Controlled BP :
                                            </Typography>

                                            <Typography variant="caption">
                                                CPT Codes 3074F or 3075F Systolic (must have one or the other of these) and 3078F or 3079F Diastolic (must have
                                                one or the other of these)
                                            </Typography>
                                        </div>
                                    }
                                    PopperProps={{ style: { zIndex: 1000 } }}
                                >
                                    <IconButton onClick={handleBpClick} size="small">
                                        <InfoIcon sx={{ width: 18, height: 18 }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Card
                                sx={{
                                    ':hover': {
                                        cursor: 'pointer',
                                        boxShadow: 10,
                                        border: 0,
                                    },
                                    boxShadow: 0,
                                }}
                                variant="outlined"
                                onClick={() => navigateToCustomDashboard('Controlled Blood Pressure')}
                            >
                                <CardContent
                                    sx={{
                                        backgroundColor: bpMetric?.IsMet ? '#82d782' : '#FD5F61',
                                        color: bpMetric?.IsMet ? 'black' : 'white',
                                    }}
                                >
                                    <Stack spacing={2} alignItems={'center'} justifyContent={'center'}>
                                        <Typography fontWeight={'bold'} variant="h4">
                                            {Boolean(bpMetric) ? bpMetric.Percentage : 0}%
                                        </Typography>
                                        <Stack direction={'row'} spacing={2}>
                                            <Typography fontWeight={'bold'} variant="body2">
                                                {bpMetric?.IsMet ? 'Met' : 'Not Met'}
                                            </Typography>
                                            <Typography fontWeight={'bold'} variant="body2">
                                                (Goal: {Boolean(bpMetric) ? bpMetric.Goal : 0}%)
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={4} textAlign="center" paddingLeft="30px">
                            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} sx={{ width: '100%' }}>
                                <Tooltip title="BMI">
                                    <Typography
                                        fontWeight="bold"
                                        variant="body1"
                                        sx={{
                                            minWidth: 40,
                                            maxWidth: '100%',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            textAlign: 'center',
                                        }}
                                    >
                                        BMI
                                    </Typography>
                                </Tooltip>
                                <Tooltip
                                    open={isBmiTooltipShow}
                                    title={
                                        <div>
                                            <Typography variant="body2">For all adults 18-74, within a rolling 12 months</Typography>

                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                BMI Completed :
                                            </Typography>

                                            <Typography variant="caption">
                                                Any of the ICD 10 codes - Z38.20, Z68, Z68.1, Z68.2, Z68.20, Z68.21, Z68.22, Z68.23, Z68.24, Z68.25, Z68.26,
                                                Z68.27, Z68.28, Z68.29, Z68.3, Z68.30, Z68.31, Z68.32, Z68.33, Z68.34, Z68.35, Z68.36, Z68.37, Z68.38, Z68.39,
                                                Z68.4, Z68.41, Z68.42, Z68.43, Z68.44, Z68.45, Z68.5, Z68.51, Z68.52, Z68.53, Z68.54
                                            </Typography>
                                        </div>
                                    }
                                    PopperProps={{ style: { zIndex: 1000 } }}
                                >
                                    <IconButton onClick={handleBmiClick}>
                                        <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Card
                                sx={{
                                    ':hover': {
                                        cursor: 'pointer',
                                        boxShadow: 10,
                                        border: 0,
                                    },
                                    boxShadow: 0,
                                }}
                                variant="outlined"
                                onClick={() => navigateToCustomDashboard('BMI')}
                            >
                                <CardContent
                                    sx={{
                                        backgroundColor: bmiMetric?.IsMet ? '#82d782' : '#FD5F61',
                                        color: bmiMetric?.IsMet ? 'black' : 'white',
                                    }}
                                >
                                    <Stack spacing={2} alignItems={'center'} justifyContent={'center'}>
                                        <Typography fontWeight={'bold'} variant="h4">
                                            {Boolean(bmiMetric) ? bmiMetric.Percentage : 0}%
                                        </Typography>
                                        <Stack direction={'row'} spacing={2}>
                                            <Typography fontWeight={'bold'} variant="body2">
                                                {bmiMetric?.IsMet ? 'Met' : 'Not Met'}
                                            </Typography>
                                            <Typography fontWeight={'bold'} variant="body2">
                                                (Goal: {Boolean(bmiMetric) ? bmiMetric.Goal : 0}%)
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={4} textAlign="center" paddingLeft="30px">
                            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} sx={{ width: '100%' }}>
                                <Tooltip title="Adult Health">
                                    <Typography
                                        fontWeight="bold"
                                        variant="body1"
                                        sx={{
                                            minWidth: 60,
                                            maxWidth: '100%',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            textAlign: 'center',
                                        }}
                                    >
                                        Adult Health
                                    </Typography>
                                </Tooltip>
                                <Tooltip
                                    open={isAdultHealthTooltipShow}
                                    title={
                                        <div>
                                            <Typography variant="body2">Member is 20 years of age or older, within a rolling 12 months</Typography>

                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                Visits Completed :
                                            </Typography>

                                            <Typography variant="caption">
                                                Any of the CPT codes - 92002, 92004, 92012, 92014, 93005, 97597, 98966, 98967, 98968, 98969, 98970, 98971,
                                                98972, 99211, 99212, 99213, 99214, 99215, 99201, 99202, 99203, 99204, 99205, 99223, 99241, 99242, 99243, 99244,
                                                99245, 99281, 99282, 99283, 99284, 99285, 99304, 99305, 99306, 99307, 99308, 99309, 99310, 99334, 99335, 99336,
                                                99337, 99341, 99342, 99343, 99344, 99345, 99347, 99348, 99349, 99350, 99381, 99382, 99383, 99384, 99385, 99386,
                                                99387, 99391, 99392, 99393, 99394, 99395, 99396, 99397, 99401, 99402, 99403, 99404, 99411, 99412, 99421, 99422,
                                                99423, 99429, 99441, 99442, 99443, 99444, 99457, 99483, G0402, G0438, G0439, G0463, T1015, H1000, G0071, G2010,
                                                G2012, G2061, G2062, G2063, G2250, G2251, G2252
                                            </Typography>
                                        </div>
                                    }
                                    PopperProps={{ style: { zIndex: 1000 } }}
                                >
                                    <IconButton onClick={handleAdultHealthClick}>
                                        <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Card
                                sx={{
                                    ':hover': {
                                        cursor: 'pointer',
                                        boxShadow: 10,
                                        border: 0,
                                    },
                                    boxShadow: 0,
                                }}
                                variant="outlined"
                                onClick={() => navigateToCustomDashboard('Adult Health')}
                            >
                                <CardContent
                                    sx={{
                                        backgroundColor: adultHealthMetric?.IsMet ? '#82d782' : '#FD5F61',
                                        color: adultHealthMetric?.IsMet ? 'black' : 'white',
                                    }}
                                >
                                    <Stack spacing={2} alignItems={'center'} justifyContent={'center'}>
                                        <Typography fontWeight={'bold'} variant="h4">
                                            {Boolean(adultHealthMetric) ? adultHealthMetric.Percentage : 0}%
                                        </Typography>
                                        <Stack direction={'row'} spacing={2}>
                                            <Typography fontWeight={'bold'} variant="body2">
                                                {adultHealthMetric?.IsMet ? 'Met' : 'Not Met'}
                                            </Typography>
                                            <Typography fontWeight={'bold'} variant="body2">
                                                (Goal: {Boolean(adultHealthMetric) ? adultHealthMetric.Goal : 0}%)
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={4} textAlign="center" paddingLeft="30px">
                            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} sx={{ width: '100%' }}>
                                <Tooltip title="Depression Screen">
                                    <Typography
                                        fontWeight="bold"
                                        variant="body1"
                                        sx={{
                                            minWidth: 60,
                                            maxWidth: '100%',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            textAlign: 'center',
                                        }}
                                    >
                                        Depression Screen
                                    </Typography>
                                </Tooltip>
                                <Tooltip
                                    open={isDepressionScreenTooltipShow}
                                    title={
                                        <div>
                                            <Typography variant="body2">For members aged 18-75, in the rolling 12 month time frame</Typography>
                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                Met :
                                            </Typography>
                                            <Typography variant="caption">CPT code G8510 OR G8431 OR G8511</Typography>
                                        </div>
                                    }
                                    PopperProps={{ style: { zIndex: 1000 } }}
                                >
                                    <IconButton onClick={handleDepressionScreenClick}>
                                        <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Card
                                sx={{
                                    ':hover': {
                                        cursor: 'pointer',
                                        boxShadow: 10,
                                        border: 0,
                                    },
                                    boxShadow: 0,
                                }}
                                variant="outlined"
                                onClick={() => navigateToCustomDashboard('Depression Screen')}
                            >
                                <CardContent
                                    sx={{
                                        backgroundColor: depressionScreenMetric?.IsMet ? '#82d782' : '#FD5F61',
                                        color: depressionScreenMetric?.IsMet ? 'black' : 'white',
                                    }}
                                >
                                    <Stack spacing={2} alignItems={'center'} justifyContent={'center'}>
                                        <Typography fontWeight={'bold'} variant="h4">
                                            {Boolean(depressionScreenMetric) ? depressionScreenMetric.Percentage : 0}%
                                        </Typography>
                                        <Stack direction={'row'} spacing={2}>
                                            <Typography fontWeight={'bold'} variant="body2">
                                                {depressionScreenMetric?.IsMet ? 'Met' : 'Not Met'}
                                            </Typography>
                                            <Typography fontWeight={'bold'} variant="body2">
                                                (Goal: {Boolean(depressionScreenMetric) ? depressionScreenMetric.Goal : 0}%)
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={4} textAlign="center" paddingLeft="30px">
                            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} sx={{ width: '100%' }}>
                                <Tooltip title="Depression Follow Up">
                                    <Typography
                                        fontWeight="bold"
                                        variant="body1"
                                        sx={{
                                            minWidth: 60,
                                            maxWidth: '100%',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            textAlign: 'center',
                                        }}
                                    >
                                        Depression Follow Up
                                    </Typography>
                                </Tooltip>
                                <Tooltip
                                    open={isDepressionFollowUpTooltipShow}
                                    title={
                                        <div>
                                            <Typography variant="body2">For members aged 18-75 within the rolling 12 month time frame</Typography>

                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                Met :
                                            </Typography>

                                            <Typography variant="caption">
                                                Had an encounter with a CPT code of G8431 within 30 days of the G8511 CPT code being entered, or if G8431 is the
                                                more recent CPT code.
                                            </Typography>
                                        </div>
                                    }
                                    PopperProps={{ style: { zIndex: 1000 } }}
                                >
                                    <IconButton onClick={handleDepressionFollowUpClick}>
                                        <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Card
                                sx={{
                                    ':hover': {
                                        cursor: 'pointer',
                                        boxShadow: 10,
                                        border: 0,
                                    },
                                    boxShadow: 0,
                                }}
                                variant="outlined"
                                onClick={() => navigateToCustomDashboard('Depression Follow Up')}
                            >
                                <CardContent
                                    sx={{
                                        backgroundColor: depressionFollowUpMetric?.IsMet ? '#82d782' : '#FD5F61',
                                        color: depressionFollowUpMetric?.IsMet ? 'black' : 'white',
                                    }}
                                >
                                    <Stack spacing={2} alignItems={'center'} justifyContent={'center'}>
                                        <Typography fontWeight={'bold'} variant="h4">
                                            {Boolean(depressionFollowUpMetric) ? depressionFollowUpMetric.Percentage : 0}%
                                        </Typography>
                                        <Stack direction={'row'} spacing={2}>
                                            <Typography fontWeight={'bold'} variant="body2">
                                                {depressionFollowUpMetric?.IsMet ? 'Met' : 'Not Met'}
                                            </Typography>
                                            <Typography fontWeight={'bold'} variant="body2">
                                                (Goal: {Boolean(depressionFollowUpMetric) ? depressionFollowUpMetric.Goal : 0}%)
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={4} textAlign="center" paddingLeft="30px">
                            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} sx={{ width: '100%' }}>
                                <Tooltip title="Medication Reconciliation">
                                    <Typography
                                        fontWeight="bold"
                                        variant="body1"
                                        sx={{
                                            minWidth: 60,
                                            maxWidth: '100%',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            textAlign: 'center',
                                        }}
                                    >
                                        Medication Reconciliation
                                    </Typography>
                                </Tooltip>
                                <Tooltip
                                    open={isMedicationTooltipShow}
                                    title={
                                        <div>
                                            <Typography variant="body2">Within 30 days of discharge from a facility</Typography>

                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                Documented :
                                            </Typography>

                                            <Typography variant="caption">CPT code 1111F</Typography>
                                        </div>
                                    }
                                    PopperProps={{ style: { zIndex: 1000 } }}
                                >
                                    <IconButton onClick={handleMedicationClick}>
                                        <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Card
                                sx={{
                                    ':hover': {
                                        cursor: 'pointer',
                                        boxShadow: 10,
                                        border: 0,
                                    },
                                    boxShadow: 0,
                                }}
                                variant="outlined"
                                onClick={() => navigateToCustomDashboard('Medication Reconciliation')}
                            >
                                <CardContent
                                    sx={{
                                        backgroundColor: medicationMetric?.IsMet ? '#82d782' : '#FD5F61',
                                        color: medicationMetric?.IsMet ? 'black' : 'white',
                                    }}
                                >
                                    <Stack spacing={2} alignItems={'center'} justifyContent={'center'}>
                                        <Typography fontWeight={'bold'} variant="h4">
                                            {Boolean(medicationMetric) ? medicationMetric.Percentage : 0}%
                                        </Typography>
                                        <Stack direction={'row'} spacing={2}>
                                            <Typography fontWeight={'bold'} variant="body2">
                                                {medicationMetric?.IsMet ? 'Met' : 'Not Met'}
                                            </Typography>
                                            <Typography fontWeight={'bold'} variant="body2">
                                                (Goal: {Boolean(medicationMetric) ? medicationMetric.Goal : 0}%)
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={4} textAlign="center" paddingLeft="30px">
                            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} sx={{ width: '100%' }}>
                                <Tooltip title="FUH">
                                    <Typography
                                        fontWeight="bold"
                                        variant="body1"
                                        sx={{
                                            minWidth: 40,
                                            maxWidth: '100%',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            textAlign: 'center',
                                        }}
                                    >
                                        FUH
                                    </Typography>
                                </Tooltip>
                                <Tooltip
                                    open={isFuhTooltipShow}
                                    title={
                                        <div>
                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                Visits within 7 Days :
                                            </Typography>
                                            <Typography variant="caption">Visits completed within 7 days of discharge</Typography>
                                        </div>
                                    }
                                    PopperProps={{ style: { zIndex: 1000 } }}
                                >
                                    <IconButton onClick={handleFuhClick}>
                                        <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Card
                                sx={{
                                    ':hover': {
                                        cursor: 'pointer',
                                        boxShadow: 10,
                                        border: 0,
                                    },
                                    boxShadow: 0,
                                }}
                                variant="outlined"
                                onClick={() => navigateToCustomDashboard('FUH')}
                            >
                                <CardContent
                                    sx={{
                                        backgroundColor: fuhMetric?.IsMet ? '#82d782' : '#FD5F61',
                                        color: fuhMetric?.IsMet ? 'black' : 'white',
                                    }}
                                >
                                    <Stack spacing={2} alignItems={'center'} justifyContent={'center'}>
                                        <Typography fontWeight={'bold'} variant="h4">
                                            {Boolean(fuhMetric) ? fuhMetric.Percentage : 0}%
                                        </Typography>
                                        <Stack direction={'row'} spacing={2}>
                                            <Typography fontWeight={'bold'} variant="body2">
                                                {fuhMetric?.IsMet ? 'Met' : 'Not Met'}
                                            </Typography>
                                            <Typography fontWeight={'bold'} variant="body2">
                                                (Goal: {Boolean(fuhMetric) ? fuhMetric.Goal : 0}%)
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={4} textAlign="center" paddingLeft="30px">
                            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} sx={{ width: '100%' }}>
                                <Tooltip title="Cervical Cancer Screening">
                                    <Typography
                                        fontWeight="bold"
                                        variant="body1"
                                        sx={{
                                            minWidth: 40,
                                            maxWidth: '100%',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            textAlign: 'center',
                                        }}
                                    >
                                        Cervical Cancer Screening
                                    </Typography>
                                </Tooltip>
                                <Tooltip
                                    open={isCervicalCancerScreenTooltipShow}
                                    title={
                                        <div>
                                            <Typography variant="body2">
                                                For females 21-64 years of age, if one of the following criteria is met, the cancer screening metric is
                                                considered to be met
                                            </Typography>
                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                Screened :
                                            </Typography>
                                            <Typography variant="caption" display="block">
                                                Last Met based on the external claim source in the last 2 years
                                            </Typography>
                                            <Typography variant="caption">
                                                Member's age 21-64 with any of the CPT codes - 88141, 88142, 88143, 88147, 88148, 88150, 88152, 88153, 88164,
                                                88165, 88166, 88167, 88174, 88175, or HCPCS G0123, G0124, G0141, G0143, G0144, G0145, G0147, G0148, P3000,
                                                P3001, Q0091 within the last 3 years
                                            </Typography>
                                            <Typography variant="caption" display="block">
                                                Member's age 30-64 with any of the CPT codes - 87624, 87625, 88141, 88142, 88143, 88147, 88148, 88150, 88152,
                                                88153, 88164, 88165, 88166, 88167, 88174, 88175, or HCPCS G0123, G0124, G0141, G0143, G0144, G0145, G0147,
                                                G0148, G0476, P3000, P3001, Q0091 within the last 5 years
                                            </Typography>

                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                Not Screened :
                                            </Typography>
                                            <Typography variant="caption">No CPT codes recorded</Typography>

                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                Exclusions :
                                            </Typography>
                                            <Typography variant="caption">Female age 65 or older</Typography>
                                            <Typography variant="caption" display="block">
                                                CPT: 57530, 57531, 57540, 57545, 57550, 57555, 57556, 58150, 58152, 58200, 58210, 58240, 58260, 58262, 58263,
                                                58267, 58270, 58275, 58280, 58285, 58290, 58291, 58292, 58293, 58294, 58548, 58550, 58552, 58553, 58554, 58570,
                                                58571, 58572, 58573, 58575, 58951, 58953, 58954, 58956, 59135, 99377, 99378
                                            </Typography>
                                            <Typography variant="caption" display="block">
                                                HCPCS: G0182, G9473-G9479, Q5003-Q5008, Q5010, S9126, T2042-T2046, G9054, M1017
                                            </Typography>
                                            <Typography variant="caption" display="block">
                                                ICD-10: Q51.5, Z90.710, Z90.712, 0UTC0ZZ, 0UTC4ZZ, 0UTC7ZZ, 0UTC8ZZ
                                            </Typography>
                                        </div>
                                    }
                                    PopperProps={{ style: { zIndex: 1000 } }}
                                >
                                    <IconButton onClick={handleCervicalCancerClick}>
                                        <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Card
                                sx={{
                                    ':hover': {
                                        cursor: 'pointer',
                                        boxShadow: 10,
                                        border: 0,
                                    },
                                    boxShadow: 0,
                                }}
                                variant="outlined"
                                onClick={() => navigateToCustomDashboard('Cervical Cancer Screen')}
                            >
                                <CardContent
                                    sx={{
                                        backgroundColor: cervicalCancerScreeningMetric?.IsMet ? '#82d782' : '#FD5F61',
                                        color: cervicalCancerScreeningMetric?.IsMet ? 'black' : 'white',
                                    }}
                                >
                                    <Stack spacing={2} alignItems={'center'} justifyContent={'center'}>
                                        <Typography fontWeight={'bold'} variant="h4">
                                            {Boolean(cervicalCancerScreeningMetric) ? cervicalCancerScreeningMetric.Percentage : 0}%
                                        </Typography>
                                        <Stack direction={'row'} spacing={2}>
                                            <Typography fontWeight={'bold'} variant="body2">
                                                {cervicalCancerScreeningMetric?.IsMet ? 'Met' : 'Not Met'}
                                            </Typography>
                                            <Typography fontWeight={'bold'} variant="body2">
                                                (Goal: {Boolean(cervicalCancerScreeningMetric) ? cervicalCancerScreeningMetric.Goal : 0}%)
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={4} textAlign="center" paddingLeft="30px">
                            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} sx={{ width: '100%' }}>
                                <Tooltip title="Colorectal Cancer Screening">
                                    <Typography
                                        fontWeight="bold"
                                        variant="body1"
                                        sx={{
                                            minWidth: 40,
                                            maxWidth: '100%',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            textAlign: 'center',
                                        }}
                                    >
                                        Colorectal Cancer Screening
                                    </Typography>
                                </Tooltip>
                                <Tooltip
                                    open={isColorectalCancerScreenTooltipShow}
                                    title={
                                        <div>
                                            <Typography variant="body2">
                                                For members 45-75 years of age, if one of the following criteria is met, the cancer screening metric is
                                                considered to be met
                                            </Typography>

                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                Screened :
                                            </Typography>
                                            <Typography variant="caption" display="block">
                                                Last Met based on the external claim source in the last 2 years
                                            </Typography>
                                            <Typography variant="caption">
                                                CPT 81528 within the measurement year or the 2 years prior to the measurement year
                                            </Typography>
                                            <Typography variant="caption" display="block">
                                                CPT 82270, 82274 or HCPCS G0328 within the measurement year
                                            </Typography>
                                            <Typography variant="caption" display="block">
                                                CPT 45330, 45331, 45332, 45333, 45334, 45335, 45337, 45338, 45340, 45341, 45342, 45346, 45347, 45349, 45350 or HCPCS
                                                G0104 within the measurement year or the 4 years prior to the measurement year
                                            </Typography>
                                            <Typography variant="caption" display="block">
                                                CPT 44388, 44389, 44390, 44391, 44392, 44393, 44394, 44401, 44402, 44403, 44404, 44405, 44406, 44407, 44408, 45378,
                                                45379, 45380, 45381, 45382, 45383, 45384, 45385, 45386, 45387, 45388, 45389, 45390, 45391, 45392, 45393, 45398 or
                                                HCPCS G0105 or G0121 within the measurement year or the 9 years prior to the measurement year
                                            </Typography>
                                            <Typography variant="caption" display="block">
                                                CPT 74261, 74262, 74263 in the measurement year or the 4 years prior to the measurement year
                                            </Typography>

                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                Not Screened :
                                            </Typography>
                                            <Typography variant="caption">No CPT codes recorded</Typography>

                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                Exclusions :
                                            </Typography>
                                            <Typography variant="caption" display="block">
                                                ICD-10: C18.0, C18.1, C18.2, C18.3, C18.4, C18.5, C18.6, C18.7, C18.8, C18.9, C19, C20, C21.2, C21.8, C78.5,
                                                Z85.038, Z85.048, ODTE0ZZ, ODTE4ZZ, ODTE7ZZ, ODTE8ZZ
                                            </Typography>
                                            <Typography variant="caption" display="block">
                                                CPT: 44150, 44151, 44152, 44153, 44155, 44156, 44157, 44158, 44210, 44211, 44212
                                            </Typography>
                                        </div>
                                    }
                                    PopperProps={{ style: { zIndex: 1000 } }}
                                >
                                    <IconButton onClick={handleColorectalCancerClick}>
                                        <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Card
                                sx={{
                                    ':hover': {
                                        cursor: 'pointer',
                                        boxShadow: 10,
                                        border: 0,
                                    },
                                    boxShadow: 0,
                                }}
                                variant="outlined"
                                onClick={() => navigateToCustomDashboard('Colorectal Cancer Screen')}
                            >
                                <CardContent
                                    sx={{
                                        backgroundColor: colorectalCancerScreeningMetric?.IsMet ? '#82d782' : '#FD5F61',
                                        color: colorectalCancerScreeningMetric?.IsMet ? 'black' : 'white',
                                    }}
                                >
                                    <Stack spacing={2} alignItems={'center'} justifyContent={'center'}>
                                        <Typography fontWeight={'bold'} variant="h4">
                                            {Boolean(colorectalCancerScreeningMetric) ? colorectalCancerScreeningMetric.Percentage : 0}%
                                        </Typography>
                                        <Stack direction={'row'} spacing={2}>
                                            <Typography fontWeight={'bold'} variant="body2">
                                                {colorectalCancerScreeningMetric?.IsMet ? 'Met' : 'Not Met'}
                                            </Typography>
                                            <Typography fontWeight={'bold'} variant="body2">
                                                (Goal: {Boolean(colorectalCancerScreeningMetric) ? colorectalCancerScreeningMetric.Goal : 0}%)
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={4} textAlign="center" paddingLeft="30px">
                            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} sx={{ width: '100%' }}>
                                <Tooltip title="BH Diagnostic">
                                    <Typography
                                        fontWeight="bold"
                                        variant="body1"
                                        sx={{
                                            minWidth: 40,
                                            maxWidth: '100%',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            textAlign: 'center',
                                        }}
                                    >
                                        BH Diagnostic
                                    </Typography>
                                </Tooltip>
                                <Tooltip
                                    open={isBhDiagnosticTooltipShow}
                                    title={
                                        <div>
                                            <Typography variant="body2">
                                                Percentage of BH primary & SMI members with a completed BH diagnostic in the past 6 months
                                            </Typography>
                                        </div>
                                    }
                                    PopperProps={{ style: { zIndex: 1000 } }}
                                >
                                    <IconButton onClick={handleBhDiagnosticClick}>
                                        <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Card
                                sx={{
                                    ':hover': {
                                        cursor: 'pointer',
                                        boxShadow: 10,
                                        border: 0,
                                    },
                                    boxShadow: 0,
                                }}
                                variant="outlined"
                                onClick={() => navigateToCustomDashboard('BH Diagnostic')}
                            >
                                <CardContent
                                    sx={{
                                        backgroundColor: bhDiagnosticForSmiMetric?.IsMet ? '#82d782' : '#FD5F61',
                                        color: bhDiagnosticForSmiMetric?.IsMet ? 'black' : 'white',
                                    }}
                                >
                                    <Stack spacing={2} alignItems={'center'} justifyContent={'center'}>
                                        <Typography fontWeight={'bold'} variant="h4">
                                            {Boolean(bhDiagnosticForSmiMetric) ? bhDiagnosticForSmiMetric.Percentage : 0}%
                                        </Typography>
                                        <Stack direction={'row'} spacing={2}>
                                            <Typography fontWeight={'bold'} variant="body2">
                                                {bhDiagnosticForSmiMetric?.IsMet ? 'Met' : 'Not Met'}
                                            </Typography>
                                            <Typography fontWeight={'bold'} variant="body2">
                                                (Goal: {Boolean(bhDiagnosticForSmiMetric) ? bhDiagnosticForSmiMetric.Goal : 0}%)
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    </>
                )}
            </Grid>
        </Paper>
    );
};

export default HedisMetricSummary;
